import React from "react";
import { Link, useLocation } from "react-router-dom";
import { CalendarDays, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { cacheCompliance } from "@/lib/accountCompliance";
import { AGE_GROUP_OPTIONS } from "@/lib/ageGroups";
import { setAnalyticsConsent } from "@/lib/privacyConsent";
import { isNativeApp } from "@/lib/runtime";
import { useComplianceStatus, useUpdateComplianceStatus } from "@/queries/account";

export default function AccountComplianceGate() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { data, isLoading, isError, refetch } = useComplianceStatus();
  const updateCompliance = useUpdateComplianceStatus();
  const [ageBracket, setAgeBracket] = React.useState("");
  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const termsRequired = data?.terms_complete !== true;

  React.useEffect(() => {
    if (!user?.id || !data) return;
    cacheCompliance(user.id, data);
    if (data.age_complete && !data.is_adult) {
      setAnalyticsConsent(false, "age_restriction", user.id);
    }
  }, [data, user?.id]);

  if (!user?.id || isLoading || data?.complete || location.pathname === "/legal") return null;

  const submit = async (event) => {
    event.preventDefault();
    if (!ageBracket) {
      toast.error("Välj din åldersgrupp.");
      return;
    }
    if (ageBracket === "under_13") {
      toast.error("Du måste ha fyllt 13 år för att använda Lapstr.");
      return;
    }
    if (termsRequired && !termsAccepted) {
      toast.error("Du måste godkänna användarvillkoren för att fortsätta.");
      return;
    }
    try {
      const next = await updateCompliance.mutateAsync({
        age_bracket: ageBracket,
        terms_accepted: termsRequired ? termsAccepted : true,
        terms_version: data?.current_terms_version,
        source: isNativeApp() ? "native_gate" : "web_gate",
      });
      cacheCompliance(user.id, next);
      if (next.age_complete && !next.is_adult) {
        setAnalyticsConsent(false, "age_restriction", user.id);
      }
      toast.success("Dina uppgifter har sparats.");
    } catch (error) {
      toast.error(error?.message || "Kunde inte spara uppgifterna.");
    }
  };

  return (
    <div className="native-v3-compliance-gate fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 px-4 py-[max(1rem,env(safe-area-inset-top))] backdrop-blur-sm">
      <section
        className="native-v3-compliance-panel w-full max-w-md border border-border bg-background p-5 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="compliance-title"
      >
        <div className="native-v3-compliance-header mb-5 flex items-start gap-3 border-b border-border pb-4">
          <div className="border-l-2 border-primary pl-3">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Kontouppdatering</span>
            <h1 id="compliance-title" className="mt-1 text-2xl font-bold text-foreground">Bekräfta dina uppgifter</h1>
          </div>
          <ShieldCheck className="ml-auto h-6 w-6 shrink-0 text-primary" aria-hidden="true" />
        </div>

        {isError ? (
          <div className="space-y-4">
            <p className="text-sm leading-relaxed text-muted-foreground">
              Uppgifterna kunde inte hämtas. Kontrollera anslutningen och försök igen.
            </p>
            <Button type="button" className="w-full" onClick={() => refetch()}>Försök igen</Button>
          </div>
        ) : (
          <form className="native-v3-compliance-form space-y-5" onSubmit={submit}>
            <p className="native-v3-compliance-intro text-sm leading-relaxed text-muted-foreground">
              Bekräfta dina uppgifter och godkänn de aktuella användarvillkoren för att fortsätta använda Lapstr.
            </p>

            <div className="native-v3-compliance-field space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <CalendarDays className="h-4 w-4 text-primary" aria-hidden="true" />
                Åldersgrupp <span>Obligatoriskt</span>
              </div>
              <div className="grid grid-cols-2 border border-border" role="radiogroup" aria-label="Åldersgrupp">
                {AGE_GROUP_OPTIONS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={ageBracket === value}
                    className={`flex min-h-11 items-center justify-center gap-1 border-b border-r border-border px-2 text-sm font-semibold text-foreground even:border-r-0 last:col-span-2 last:border-b-0 last:border-r-0 ${ageBracket === value ? "bg-primary/10 shadow-[inset_0_-2px_0_hsl(var(--primary))]" : "bg-background"}`}
                    onClick={() => setAgeBracket(value)}
                  >
                    {ageBracket === value ? <Check className="h-4 w-4" aria-hidden="true" /> : null}
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {termsRequired ? (
              <label className="native-v3-compliance-terms flex cursor-pointer items-start gap-3 border-y border-border py-4 text-sm leading-relaxed text-muted-foreground">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                  checked={termsAccepted}
                  onChange={(event) => setTermsAccepted(event.target.checked)}
                />
                <span>
                  Jag accepterar Lapstrs aktuella{" "}
                  <Link to="/legal#terms" className="font-semibold text-foreground underline underline-offset-4">
                    användarvillkor
                  </Link>{" "}
                  och bekräftar att jag har tagit del av{" "}
                  <Link to="/legal#privacy" className="font-semibold text-foreground underline underline-offset-4">
                    integritetspolicyn
                  </Link>.
                </span>
              </label>
            ) : null}

            <div className="native-v3-compliance-actions space-y-2">
              <Button type="submit" className="w-full" disabled={updateCompliance.isPending}>
                {updateCompliance.isPending ? "Sparar..." : "Bekräfta och fortsätt"}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={logout}>
                Logga ut
              </Button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
